/**
 * Browser WebRTC client for OpenAI Realtime API (GA, May 2026 — model
 * `gpt-realtime-2`).
 *
 * Flow:
 *   1. POST /api/realtime/session     →   ephemeral { client_secret.value, model }
 *      Body: { mode: "vad" | "ptt" } — picks server_vad vs manual commit.
 *   2. RTCPeerConnection + getUserMedia(audio) + dataChannel("oai-events").
 *   3. POST offer.sdp to https://api.openai.com/v1/realtime/calls
 *      with Authorization: Bearer <ephemeral>, Content-Type: application/sdp.
 *   4. Set returned SDP as remote description.
 *
 * Lifecycle:
 *   - 8-second connect watchdog. If pc.connectionState hasn't reached
 *     "connected" in time, fail → caller's auto-fallback chain takes over.
 *   - PTT: caller invokes `commitTurn()` on key/button release. We send
 *     `input_audio_buffer.commit` then `response.create` over the data
 *     channel. If the assistant is currently speaking, we first send
 *     `response.cancel` + `input_audio_buffer.clear` to interrupt.
 *
 * Security: the raw OpenAI API key NEVER reaches this file — only the
 * short-lived ephemeral client secret minted by our backend.
 */

const OPENAI_REALTIME_CALLS = "https://api.openai.com/v1/realtime/calls";
const SNIPPET_LEN = 240;
const CONNECT_TIMEOUT_MS = 8_000;

export class RealtimeCall {
  constructor({ apiBase, mode = "vad", onState, onEvent, onError }) {
    this.apiBase = apiBase;
    this.mode = mode === "ptt" ? "ptt" : "vad";
    this.onState = onState || (() => {});
    this.onEvent = onEvent || (() => {});
    this.onError = onError || (() => {});

    this.pc = null;
    this.dc = null;
    this.localStream = null;
    this.audioEl = null;
    this.ended = false;
    this.connected = false;
    this.assistantSpeaking = false;
    this._connectTimer = null;
  }

  _setState(state, detail) {
    if (this.ended && state !== "ended" && state !== "error") return;
    this.onState(state, detail);
  }

  async start() {
    this._setState("connecting");

    // 1. Mint ephemeral session from our backend.
    let session;
    try {
      const r = await fetch(`${this.apiBase}/api/realtime/session`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mode: this.mode }),
      });
      if (!r.ok) {
        const txt = await r.text();
        this._fail({
          stage: "session",
          httpStatus: r.status,
          snippet: txt.slice(0, SNIPPET_LEN),
          rawError: new Error(`Session mint failed (${r.status})`),
        });
        return;
      }
      session = await r.json();
    } catch (err) {
      this._fail({ stage: "session", rawError: err });
      return;
    }

    const ephemeral = session?.client_secret?.value;
    if (!ephemeral) {
      this._fail({
        stage: "session",
        snippet: "Backend response did not include client_secret.value.",
        rawError: new Error("No client_secret.value"),
      });
      return;
    }

    // 2. Mic — surface permission errors clearly.
    try {
      this.localStream = await navigator.mediaDevices.getUserMedia({ audio: true });
    } catch (err) {
      this._fail({ stage: "mic", rawError: err });
      return;
    }

    // 3. PeerConnection
    this.pc = new RTCPeerConnection();

    this.pc.onconnectionstatechange = () => {
      if (!this.pc) return;
      const s = this.pc.connectionState;
      if (s === "connected") {
        this.connected = true;
        clearTimeout(this._connectTimer);
        // For VAD mode, server emits speech_started events; for PTT we sit
        // in a generic "listening" until the user holds the button.
        this._setState("listening");
      } else if (s === "failed") {
        this._fail({ stage: "webrtc", rawError: new Error("Peer connection failed.") });
      } else if (s === "disconnected" || s === "closed") {
        if (!this.ended) this._setState("ended");
      }
    };

    // Remote audio
    this.audioEl = document.createElement("audio");
    this.audioEl.autoplay = true;
    this.audioEl.setAttribute("data-testid", "remote-audio");
    document.body.appendChild(this.audioEl);
    this.pc.ontrack = (ev) => {
      this.audioEl.srcObject = ev.streams[0];
    };

    // Local mic track
    for (const track of this.localStream.getTracks()) {
      this.pc.addTrack(track, this.localStream);
    }

    // Data channel for events (created BEFORE the offer so it's negotiated).
    this.dc = this.pc.createDataChannel("oai-events");
    this.dc.onmessage = (ev) => this._onDataMessage(ev);

    // 4. SDP offer → OpenAI GA `/v1/realtime/calls`
    let answerSdp;
    try {
      const offer = await this.pc.createOffer();
      await this.pc.setLocalDescription(offer);

      const sdpRes = await fetch(OPENAI_REALTIME_CALLS, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${ephemeral}`,
          "Content-Type": "application/sdp",
        },
        body: offer.sdp,
      });
      if (!sdpRes.ok) {
        const errText = await sdpRes.text();
        let code = null;
        let openaiType = null;
        let message = null;
        try {
          const j = JSON.parse(errText);
          code = j?.error?.code || null;
          openaiType = j?.error?.type || null;
          message = j?.error?.message || null;
        } catch { /* not JSON */ }
        this._fail({
          stage: "sdp",
          httpStatus: sdpRes.status,
          code, openaiType, message,
          snippet: errText.slice(0, SNIPPET_LEN),
          rawError: new Error(`SDP exchange failed (${sdpRes.status})`),
        });
        return;
      }
      answerSdp = await sdpRes.text();
    } catch (err) {
      this._fail({ stage: "sdp", rawError: err });
      return;
    }

    try {
      await this.pc.setRemoteDescription({ type: "answer", sdp: answerSdp });
    } catch (err) {
      this._fail({ stage: "sdp", rawError: err });
      return;
    }

    // 8s watchdog — caller-side auto-fallback fires if we don't reach
    // "connected" by then.
    this._connectTimer = setTimeout(() => {
      if (!this.connected && !this.ended) {
        this._fail({
          stage: "webrtc",
          code: "connect_timeout",
          rawError: new Error("Voice connection did not establish in time."),
        });
      }
    }, CONNECT_TIMEOUT_MS);
  }

  _onDataMessage(ev) {
    let msg = null;
    try { msg = JSON.parse(ev.data); } catch { /* ignore non-JSON */ }
    if (!msg) return;
    this.onEvent(msg);

    const t = msg.type || "";
    if (t === "input_audio_buffer.speech_started") {
      this._setState("listening");
    } else if (t === "input_audio_buffer.speech_stopped") {
      this._setState("thinking");
    } else if (t === "response.created") {
      this._setState("thinking");
    } else if (t === "response.audio.delta" || t === "response.output_audio.delta") {
      this.assistantSpeaking = true;
      this._setState("speaking");
    } else if (t === "response.done" || t === "response.completed" || t === "response.audio.done") {
      this.assistantSpeaking = false;
      // PTT returns to idle (waiting for next hold); VAD waits for next speech.
      this._setState(this.mode === "ptt" ? "idle" : "listening");
    } else if (t === "error") {
      const e = msg.error || {};
      this._fail({
        stage: "openai",
        code: e.code || e.type || "openai_error",
        openaiType: e.type,
        message: e.message,
        param: e.param,
        eventId: msg.event_id,
        snippet: JSON.stringify(msg).slice(0, SNIPPET_LEN),
        rawError: new Error(e.message || "OpenAI realtime error event"),
      });
    }
  }

  _send(obj) {
    try {
      if (this.dc && this.dc.readyState === "open") {
        this.dc.send(JSON.stringify(obj));
      }
    } catch { /* noop */ }
  }

  /**
   * PTT: called when the user releases the Hold-to-Speak button. If the
   * assistant is currently speaking, we interrupt cleanly first.
   */
  commitTurn() {
    if (this.mode !== "ptt") return;
    if (!this.connected) return;
    if (this.assistantSpeaking) {
      this._send({ type: "response.cancel" });
      this._send({ type: "input_audio_buffer.clear" });
      this.assistantSpeaking = false;
    }
    this._send({ type: "input_audio_buffer.commit" });
    this._send({ type: "response.create" });
    this._setState("thinking");
  }

  /** PTT: optional — caller may pre-warm UI when user starts holding. */
  beginTurn() {
    if (this.mode !== "ptt") return;
    if (this.assistantSpeaking) {
      this._send({ type: "response.cancel" });
      this._send({ type: "input_audio_buffer.clear" });
      this.assistantSpeaking = false;
    }
    this._setState("recording");
  }

  /**
   * Append a one-shot closing instruction to the model's system prompt.
   * Used at ~30s of trial remaining so Jesus can wrap up in-character and
   * ask the user to leave their email instead of being cut off cold.
   */
  injectClosingNote(text) {
    if (!text || !this.dc) return;
    this._send({
      type: "session.update",
      session: {
        instructions: text,   // Realtime GA: this APPENDS to the running session
      },
    });
  }

  end() {
    this.ended = true;
    clearTimeout(this._connectTimer);
    try { this.dc?.close(); } catch { /* noop */ }
    try { this.pc?.getSenders().forEach((s) => s.track && s.track.stop()); } catch { /* noop */ }
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { this.pc?.close(); } catch { /* noop */ }
    if (this.audioEl) {
      try { this.audioEl.srcObject = null; } catch { /* noop */ }
      try { this.audioEl.remove(); } catch { /* noop */ }
    }
    this.dc = null;
    this.pc = null;
    this.localStream = null;
    this.audioEl = null;
    this._setState("ended");
  }

  _fail(info) {
    if (this.ended) return;
    clearTimeout(this._connectTimer);
    const detail = {
      stage: info.stage,
      httpStatus: info.httpStatus ?? null,
      code: info.code ?? null,
      openaiType: info.openaiType ?? null,
      message: info.message ?? (info.rawError?.message || null),
      snippet: info.snippet ?? null,
      param: info.param ?? null,
      eventId: info.eventId ?? null,
      headline: explainStage(info.stage, info.rawError, info.httpStatus, info.code),
    };
    this.onError(detail);
    this._setState("error", detail.headline);
    // Best-effort cleanup without re-emitting "ended".
    try { this.dc?.close(); } catch { /* noop */ }
    try { this.localStream?.getTracks().forEach((t) => t.stop()); } catch { /* noop */ }
    try { this.pc?.close(); } catch { /* noop */ }
  }
}

export function explainStage(stage, err, httpStatus, code) {
  const name = err?.name || "";
  if (stage === "mic") {
    if (name === "NotAllowedError" || name === "SecurityError") {
      return "Microphone access is required for voice calls — please allow mic in your browser settings and try again.";
    }
    if (name === "NotFoundError" || name === "OverconstrainedError") {
      return "No microphone was detected. Please connect a mic and try again.";
    }
    return "Could not access the microphone. Please check your browser permissions.";
  }
  if (stage === "session") {
    if (httpStatus) return `Could not start the session (backend HTTP ${httpStatus}).`;
    return "Could not start the session. Please try again in a moment.";
  }
  if (stage === "sdp") {
    if (httpStatus === 401 || httpStatus === 403) return `OpenAI rejected the call (HTTP ${httpStatus}). Realtime API access may not be enabled for this project.`;
    if (httpStatus === 429 || code === "rate_limit_exceeded") return "OpenAI rate-limited or quota exhausted (HTTP 429).";
    if (httpStatus) return `OpenAI rejected the connection (HTTP ${httpStatus}${code ? ` · ${code}` : ""}).`;
    return "Could not connect to the voice service. Please try again.";
  }
  if (stage === "webrtc") {
    if (code === "connect_timeout") return "Voice connection timed out — switching to backup voice.";
    return "Voice connection dropped. Please try again.";
  }
  if (stage === "openai") {
    if (code === "rate_limit_exceeded" || code === "insufficient_quota") return "OpenAI quota / rate limit reached.";
    if (code === "billing_hard_limit_reached") return "OpenAI billing limit reached.";
    if (code) return `OpenAI error: ${code}`;
    return err?.message || "The voice service returned an error.";
  }
  return err?.message || "Something went wrong.";
}
